import {ExtraData, Severity} from './types';
import type {Reporter, ReportOpts} from './Reporter';
import {Redactor} from './redactor/Redactor';

type ReporterConfig = [Reporter, Severity[]] | Reporter;

export type MonitorConfiguration = {
    reporters: ReporterConfig[];
};

export type MonitorReportOptions = {
    piiFields?: string[];
};

/**
 * A class capable of notifying user errors and warnings
 */
export class Monitor {
    private readonly _reporters: [Reporter, Severity[]][];

    constructor(
        private readonly _configuration: MonitorConfiguration,
        private readonly _redactor: Redactor = new Redactor()
    ) {
        this._reporters = this._configuration.reporters.map(r => (Array.isArray(r) ? r : [r, Object.values(Severity)]));
    }

    /**
     * Reports an error
     *
     * @param message The error message
     * @param error The error
     * @param extra Extra data object to be attached to the report
     */
    error(message: string, error: Error, extra?: ExtraData, options?: MonitorReportOptions) {
        this.report({severity: Severity.Error, message, error, extra}, options);
    }

    /**
     * Reports a warning
     *
     * @param message The warning message
     * @param error The error
     * @param extra Extra data object to be attached to the report
     */
    warning(message: string, error: Error, extra?: ExtraData, options?: MonitorReportOptions) {
        this.report({severity: Severity.Warning, message, error, extra}, options);
    }

    /**
     * Reports an informational message
     *
     * @param message The informational message
     * @param extra Extra data object to be attached to the report
     */
    info(message: string, extra?: ExtraData, options?: MonitorReportOptions) {
        this.report({severity: Severity.Info, message, extra}, options);
    }

    private report({severity, extra, ...rest}: ReportOpts, options?: MonitorReportOptions) {
        const redactedExtra = this._redactor.redact(extra ?? {}, {piiFields: options?.piiFields ?? []});
        this._reporters
            .filter(([r, s]) => s.includes(severity))
            .map(([r]) => r.report({severity, extra: redactedExtra, ...rest}));
    }
}
